<template>
  <div class="svoi-swiper-container">
    <SvoiSwiperBase class="svoi-swiper" v-bind="$props">
      <template #item="{ item }">
        <slot :item="item" name="item" />
      </template>

      <template
        #after="{ swipe, swipeOffset, items, currentBreakpoint, swipeSteps, virtualizedItems, sortedItems, isInfinite }"
      >
        <div class="controls">
          <slot name="controls" :swipe-offset="swipeOffset" :swipe-steps="swipeSteps" />
          <div v-if="showIndicators" class="bottom-control">
            <template v-if="isInfinite">
              <span
                v-for="(item, idx) of sortedItems"
                :key="item.id"
                :class="['offset-control', { '-active': virtualizedItems[Math.round(swipeOffset)].order === idx }]"
                @click="swipeSteps(getSwipeStepsVirtual(Number(idx), virtualizedItems, swipeOffset))"
              />
            </template>
            <template v-else>
              <span
                v-for="(item, idx) of getOffsetControls(items, currentBreakpoint.countVisible)"
                :key="item.id"
                :class="['offset-control', { '-active': idx === Math.round(swipeOffset) }]"
                @click="swipe(idx)"
              />
            </template>
          </div>
        </div>
      </template>
    </SvoiSwiperBase>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi'
import type { SwiperItemProp } from '@svoi-ui/interfaces/swiper'
import { SvoiSwiperBase } from '.'

export default defineComponent({
  name: 'SvoiSwiper',
  components: { SvoiSwiperBase },

  props: {
    ...SvoiSwiperBase.props,

    showIndicators: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const getOffsetControls = <T extends SwiperItemProp>(arr: Array<T>, countVisible: number): Array<T> => {
      if (!Array.isArray(arr)) {
        return []
      }

      const to = arr.length + 1 - countVisible

      return to > 0 ? arr.slice(0, to) : []
    }

    const getActiveVirtualItemIndex = (swipeOffset: number, virtualizedItems: Array<SwiperItemProp>): number => {
      const currentItem = virtualizedItems[Math.round(swipeOffset)]

      if (!currentItem || !Number.isInteger(swipeOffset)) {
        return -1
      }

      return currentItem.order ?? -1
    }

    const getSwipeStepsVirtual = (
      targetElement: number,
      virtualizedItems: Array<SwiperItemProp>,
      swipeOffset: number
    ): number | null => {
      const currentElement = virtualizedItems[Math.round(swipeOffset)]?.order ?? 0

      return signedDistance(currentElement, targetElement, virtualizedItems.length)
    }

    const signedDistance = (v1: number, v2: number, length: number) => {
      const directDiff = v2 - v1
      const cyclicDiff = directDiff - Math.sign(directDiff) * length

      return Math.abs(directDiff) <= length / 2 ? directDiff : cyclicDiff
    }

    return { getOffsetControls, getActiveVirtualItemIndex, getSwipeStepsVirtual }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/swiper.scss';
</style>
